import { Button, Menu } from '@mantine/core';
import {
  IconBuilding,
  IconCheck,
  IconChevronDown,
  IconCloudUpload,
  IconLibrary,
  IconLock,
  IconShip,
} from '@tabler/icons-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './Documents.module.css';
import { formatFileSize, formatStorageSize } from './library.js';
import type { VesselFileUpload, VesselUploadSummary } from './types.js';
import { COMPANY_DOCUMENTS, type ShipUploadMarker, uploadLabel } from './vessel-library.js';

/**
 * Gemi kütüphanesinin ortak parçaları (M8a4e; ADR 0036, ADR 0038 §1, §3, §12, §14): web ofiste
 * kütüphane seçici, gemi yükleme özeti, salt okunur şerit ve "dosya henüz gemide" kartı; gemide
 * revizyon satırının yükleme işareti. Müşteri tasarımında yoklar; aynı dilde çizilir.
 */

/**
 * Kütüphane seçici (breadcrumb satırı): "Company documents" ve her geminin kütüphanesi. Her
 * genişlikte menü düğmesidir: dar ekranda da sığar, klavyeyle açılır ve ok tuşlarıyla gezilir.
 * Escape her an kapatır: menü açılırken odak henüz düğmedeyken basılan Escape de (Mantine
 * Escape'i yalnız açılır listede dinler; hızlı Enter + Escape'te menü açık kalıyordu).
 */
export function LibraryPicker({
  value,
  vessels,
  onChange,
}: {
  value: string;
  vessels: readonly { id: string; name: string }[];
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.library.picker.${key}`, options ?? {});
  const current = vessels.find((v) => v.id === value);
  const label = current ? k('vessel', { name: current.name }) : k('company');
  const [opened, setOpened] = useState(false);
  const item = (id: string, text: string, icon: ReactNode) => (
    <Menu.Item
      key={id}
      leftSection={icon}
      rightSection={id === value ? <IconCheck size={14} aria-hidden /> : null}
      aria-current={id === value ? 'true' : undefined}
      onClick={() => onChange(id)}
    >
      {text}
    </Menu.Item>
  );
  return (
    <Menu position="bottom-end" withinPortal opened={opened} onChange={setOpened}>
      <Menu.Target>
        <Button
          variant="default"
          size="xs"
          onKeyDownCapture={(event) => {
            if (event.key !== 'Escape' || !opened) return;
            event.preventDefault();
            setOpened(false);
          }}
          className={classes.libraryPicker}
          leftSection={<IconLibrary size={15} />}
          rightSection={<IconChevronDown size={14} />}
          aria-label={`${k('label')}: ${label}`}
        >
          <span className={classes.libraryPickerLabel}>{label}</span>
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {item(COMPANY_DOCUMENTS, k('company'), <IconBuilding size={15} />)}
        {vessels.length > 0 ? <Menu.Label>{k('vessels')}</Menu.Label> : null}
        {vessels.map((vessel) =>
          item(vessel.id, k('vessel', { name: vessel.name }), <IconShip size={15} />),
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

/** Geminin yükleme özeti (`vessel_file_status.upload_*`): bekleyen, başarısız ve durum. */
export function UploadSummary({ summary }: { summary: VesselUploadSummary | null | undefined }) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.library.summary.${key}`, options ?? {});
  const parts: string[] = [];
  if (!summary) parts.push(k('notReported'));
  else {
    if (summary.pendingFiles > 0) {
      parts.push(
        k('pending', {
          count: summary.pendingFiles,
          size: formatStorageSize(summary.pendingBytes, i18n.language),
        }),
      );
    }
    if (summary.failedFiles > 0) parts.push(k('failed', { count: summary.failedFiles }));
    parts.push(k(`states.${summary.state}`, { defaultValue: summary.state }));
  }
  return (
    <p className={classes.uploadSummary} data-failed={(summary?.failedFiles ?? 0) > 0 || undefined}>
      <IconCloudUpload size={15} aria-hidden />
      <span className={classes.uploadSummaryTitle}>{k('title')}:</span>
      <span>{parts.join(' · ')}</span>
    </p>
  );
}

/**
 * Salt okunur şerit. Ofiste gemi görünümünde: geminin kütüphanesini gördüğü gibi, revizyon şirket
 * dokümanlarında ("Open company documents"); taslaksa "Draft (on vessel)". Geminin eski belgesinde
 * (ADR 0081'den önce gemide doğmuş; `vessel`) ofiste ve gemide bunun notu: gemide revize
 * edilemez, denetimli sürümü şirket yayınlar.
 */
export function ReadOnlyStrip({
  draft,
  vessel = false,
  place = 'office',
  onOpenCompany,
}: {
  draft: boolean;
  vessel?: boolean | undefined;
  place?: 'office' | 'ship' | undefined;
  onOpenCompany?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`documents.library.${key}`);
  const lines = [
    ...(place === 'office' ? [k('readOnly')] : []),
    ...(vessel ? [k(place === 'office' ? 'legacyOffice' : 'legacyVessel')] : []),
  ];
  return (
    <section className={classes.changes} data-tone="info" aria-label={lines[0]}>
      <span className={classes.changesIcon} aria-hidden>
        <IconLock size={20} />
      </span>
      <div className={classes.changesText}>
        {lines.map((line) => (
          <p key={line} className={classes.changesBody}>
            {line}
          </p>
        ))}
        {/* Metnin altında: dar sütunda düğme sıkışıp kesilmez */}
        {place === 'office' && onOpenCompany ? (
          <Button
            size="xs"
            variant="light"
            mt={8}
            leftSection={<IconBuilding size={15} />}
            onClick={onOpenCompany}
          >
            {k('openCompany')}
          </Button>
        ) : null}
      </div>
      {draft ? <StatusBadge tone="warning">{k('draftOnVessel')}</StatusBadge> : null}
    </section>
  );
}

/** Dosyanın ofisteki durumu: "In office", "On vessel – uploading 42%" … (ADR 0038 §14). */
export function UploadStatusBadge({ upload }: { upload: VesselFileUpload | null | undefined }) {
  const { t } = useTranslation();
  const label = uploadLabel(upload);
  return (
    <StatusBadge tone={label.tone}>
      {t(`documents.library.upload.${label.key}`, label.values)}
    </StatusBadge>
  );
}

/** Gemide revizyon satırının yükleme işareti: Waiting to upload, Uploading, In office. */
export function ShipUploadMarkerBadge({ marker }: { marker: ShipUploadMarker }) {
  const { t } = useTranslation();
  const tone = marker === 'inOffice' ? 'success' : marker === 'failed' ? 'danger' : 'neutral';
  return <StatusBadge tone={tone}>{t(`documents.library.marker.${marker}`)}</StatusBadge>;
}

/**
 * Ofiste "dosya henüz gemide" kartı (önizleyicinin yerine): gemi dosyayı yükleyip ofis
 * doğrulamadıkça açılmaz ve indirilmez (`FILE_NOT_UPLOADED`; ADR 0038 §14).
 */
export function NotUploadedCard({
  upload,
  size,
}: {
  upload: VesselFileUpload | null | undefined;
  size: number;
}) {
  const { t, i18n } = useTranslation();
  const k = (key: string) => t(`documents.library.notUploaded.${key}`);
  return (
    <div className={classes.notOnBoard} role="status">
      <span className={classes.notOnBoardIcon} aria-hidden>
        <IconCloudUpload size={28} stroke={1.6} />
      </span>
      <p className={classes.notOnBoardTitle}>{k('title')}</p>
      <p className={classes.notOnBoardText}>{k('description')}</p>
      <dl className={classes.notOnBoardFacts} data-columns="2">
        <div>
          <dt>{k('status')}</dt>
          <dd>
            <UploadStatusBadge upload={upload} />
          </dd>
        </div>
        <div>
          <dt>{k('size')}</dt>
          <dd>{formatFileSize(size, i18n.language)}</dd>
        </div>
      </dl>
    </div>
  );
}
